import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { 
  Search, 
  Smartphone, 
  Eye, 
  Crown, 
  AlertCircle,
  RefreshCw,
  PlusCircle,
  FileDown,
  Camera,
  Calendar,
  X
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CommandQueue = () => {
  const [issues, setIssues] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportDate, setExportDate] = useState("");
  const [filters, setFilters] = useState({
    status: "",
    severity: "",
    department: "",
    dateRange: "",
    startDate: "",
    endDate: "",
    search: "",
  });

  const fetchIssues = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.status && filters.status !== "all") params.append("status", filters.status);
      if (filters.severity && filters.severity !== "all") params.append("severity", filters.severity);
      if (filters.department && filters.department !== "all") params.append("department", filters.department);
      if (filters.dateRange && filters.dateRange !== "all" && filters.dateRange !== "custom") {
        params.append("date_range", filters.dateRange);
      }
      if (filters.dateRange === "custom" && filters.startDate) {
        params.append("start_date", filters.startDate);
      }
      if (filters.dateRange === "custom" && filters.endDate) {
        params.append("end_date", filters.endDate);
      }
      
      const res = await axios.get(`${API}/issues?${params.toString()}`);
      setIssues(res.data);
    } catch (err) {
      toast.error("Failed to load issues");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIssues();
  }, [filters.status, filters.severity, filters.department, filters.dateRange, filters.startDate, filters.endDate]);

  const handleExportPDF = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (filters.status && filters.status !== "all") params.append("status", filters.status);
      if (filters.severity && filters.severity !== "all") params.append("severity", filters.severity);
      if (filters.department && filters.department !== "all") params.append("department", filters.department);
      if (exportDate) {
        params.append("specific_date", exportDate);
      } else if (filters.dateRange && filters.dateRange !== "all") {
        params.append("date_range", filters.dateRange);
      }
      
      const response = await axios.get(`${API}/issues/export/pdf?${params.toString()}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `orbit_briefing_${exportDate || new Date().toISOString().slice(0,10)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("PDF exported successfully");
      setShowExportModal(false);
      setExportDate("");
    } catch (err) {
      toast.error("Failed to export PDF");
    } finally {
      setExporting(false);
    }
  };

  const getSeverityColor = (severity) => {
    const colors = {
      S4: "bg-red-500/10 text-red-500 border-red-500/20",
      S3: "bg-amber-500/10 text-amber-500 border-amber-500/20",
      S2: "bg-blue-500/10 text-blue-500 border-blue-500/20",
      S1: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20",
      P1: "bg-purple-500/10 text-purple-500 border-purple-500/20",
    };
    return colors[severity] || colors.S2;
  };

  const getStatusColor = (status) => {
    const colors = {
      OPEN: "bg-red-500/10 text-red-500 border-red-500/20",
      ACKNOWLEDGED: "bg-amber-500/10 text-amber-500 border-amber-500/20",
      IN_PROGRESS: "bg-blue-500/10 text-blue-500 border-blue-500/20",
      RESOLVED: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20",
      CLOSED: "bg-zinc-500/10 text-zinc-400 border-zinc-500/20",
    };
    return colors[status] || colors.OPEN;
  };

  const getRiskColor = (level) => {
    const colors = {
      CRITICAL: "text-red-500",
      HIGH: "text-amber-500",
      MEDIUM: "text-blue-500",
      LOW: "text-emerald-500",
    };
    return colors[level] || "text-muted-foreground";
  };

  const getGuestStatusColor = (status) => {
    const colors = {
      ARRIVAL: "bg-blue-500/10 text-blue-400 border-blue-500/20",
      IN_HOUSE: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
      DEPARTURE: "bg-amber-500/10 text-amber-400 border-amber-500/20",
    };
    return colors[status] || colors.IN_HOUSE;
  };

  const getGuestStatusLabel = (status) => {
    const labels = { ARRIVAL: "Arrival", IN_HOUSE: "In-House", DEPARTURE: "Departure" };
    return labels[status] || status;
  };

  const filteredIssues = issues.filter((issue) => {
    if (!filters.search) return true;
    const search = filters.search.toLowerCase();
    return (
      issue.issue_number.toLowerCase().includes(search) ||
      issue.guest_name.toLowerCase().includes(search) ||
      issue.room_number.toLowerCase().includes(search) ||
      issue.description.toLowerCase().includes(search)
    );
  });

  const highlightText = (text) => {
    const defectWords = /\b(mati|rusak|bocor|kotor|bau|dingin|hambar|lama|telat|salah|macet|error|broken|dirty|slow|wrong)\b/gi;
    const praiseWords = /\b(bagus|baik|ramah|cepat|bersih|enak|lezat|mantap|helpful|good|clean|fast|great|excellent|menikmati)\b/gi;
    
    let result = text;
    result = result.replace(defectWords, '<span class="highlight-defect">$1</span>');
    result = result.replace(praiseWords, '<span class="highlight-praise">$1</span>');
    
    return <span dangerouslySetInnerHTML={{ __html: result }} />;
  };

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl font-bold tracking-tight uppercase">
              Command Queue
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {filteredIssues.length} active issues
            </p>
          </div>
          <div className="flex gap-2">
            <Button onClick={fetchIssues} variant="outline" size="sm" data-testid="refresh-issues">
              <RefreshCw size={14} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button 
              onClick={() => setShowExportModal(true)} 
              variant="outline" 
              size="sm" 
              disabled={exporting}
              data-testid="export-pdf-btn"
            >
              <FileDown size={14} className={`mr-2 ${exporting ? 'animate-pulse' : ''}`} />
              {exporting ? 'Exporting...' : 'Export PDF'}
            </Button>
            <Link to="/new-issue">
              <Button size="sm" className="btn-primary" data-testid="new-issue-btn">
                <PlusCircle size={14} className="mr-2" />
                New Issue
              </Button>
            </Link>
          </div>
        </div>

        {/* Export PDF Modal */}
        {showExportModal && (
          <Card className="orbit-card border-primary/30">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading text-sm uppercase tracking-wider">Export PDF</h3>
                <Button variant="ghost" size="sm" onClick={() => setShowExportModal(false)}>
                  <X size={16} />
                </Button>
              </div>
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                    <Calendar size={12} /> Pilih Tanggal Spesifik (Opsional)
                  </label>
                  <Input
                    type="date"
                    value={exportDate}
                    onChange={(e) => setExportDate(e.target.value)}
                    className="bg-background"
                    data-testid="export-date-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Kosongkan untuk menggunakan filter tanggal yang aktif
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button 
                    onClick={handleExportPDF} 
                    disabled={exporting}
                    className="flex-1"
                    data-testid="confirm-export-btn"
                  >
                    <FileDown size={14} className={`mr-2 ${exporting ? 'animate-pulse' : ''}`} />
                    {exporting ? 'Exporting...' : 'Download PDF'}
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => { setShowExportModal(false); setExportDate(""); }}
                  >
                    Batal
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <Card className="orbit-card">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search issues..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className="pl-9 bg-background"
                  data-testid="search-input"
                />
              </div>
              <div className="flex gap-2">
                <Select
                  value={filters.status}
                  onValueChange={(value) => setFilters({ ...filters, status: value })}
                >
                  <SelectTrigger className="w-[140px] bg-background" data-testid="filter-status">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="OPEN">Open</SelectItem>
                    <SelectItem value="ACKNOWLEDGED">Acknowledged</SelectItem>
                    <SelectItem value="IN_PROGRESS">In Progress</SelectItem>
                    <SelectItem value="RESOLVED">Resolved</SelectItem>
                    <SelectItem value="CLOSED">Closed</SelectItem>
                  </SelectContent>
                </Select>
                <Select
                  value={filters.dateRange}
                  onValueChange={(value) => setFilters({ ...filters, dateRange: value })}
                >
                  <SelectTrigger className="w-[140px] bg-background" data-testid="filter-date">
                    <SelectValue placeholder="Date" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Time</SelectItem>
                    <SelectItem value="today">Today</SelectItem>
                    <SelectItem value="yesterday">Yesterday</SelectItem>
                    <SelectItem value="week">Last 7 Days</SelectItem>
                    <SelectItem value="month">Last 30 Days</SelectItem>
                    <SelectItem value="3months">Last 3 Months</SelectItem>
                    <SelectItem value="6months">Last 6 Months</SelectItem>
                    <SelectItem value="year">Last 1 Year</SelectItem>
                    <SelectItem value="custom">Custom Range...</SelectItem>
                  </SelectContent>
                </Select>
                {filters.dateRange === "custom" && (
                  <div className="flex gap-2 items-center">
                    <Input
                      type="date"
                      value={filters.startDate || ""}
                      onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                      className="w-[140px] bg-background"
                      placeholder="Start"
                    />
                    <span className="text-muted-foreground">-</span>
                    <Input
                      type="date"
                      value={filters.endDate || ""}
                      onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                      className="w-[140px] bg-background"
                      placeholder="End"
                    />
                  </div>
                )}
                <Select
                  value={filters.severity}
                  onValueChange={(value) => setFilters({ ...filters, severity: value })}
                >
                  <SelectTrigger className="w-[140px] bg-background" data-testid="filter-severity">
                    <SelectValue placeholder="Severity" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Severity</SelectItem>
                    <SelectItem value="S4">S4 - Critical</SelectItem>
                    <SelectItem value="S3">S3 - High</SelectItem>
                    <SelectItem value="S2">S2 - Medium</SelectItem>
                    <SelectItem value="S1">S1 - Low</SelectItem>
                    <SelectItem value="P1">P1 - Praise</SelectItem>
                  </SelectContent>
                </Select>
                <Select
                  value={filters.department}
                  onValueChange={(value) => setFilters({ ...filters, department: value })}
                >
                  <SelectTrigger className="w-[140px] bg-background" data-testid="filter-department">
                    <SelectValue placeholder="Department" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Dept</SelectItem>
                    <SelectItem value="ENG">Engineering</SelectItem>
                    <SelectItem value="HK">Housekeeping</SelectItem>
                    <SelectItem value="FNB">F&B</SelectItem>
                    <SelectItem value="FO">Front Office</SelectItem>
                    <SelectItem value="SEC">Security</SelectItem>
                    <SelectItem value="IT">IT</SelectItem>
                    <SelectItem value="MGT">Management</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Issues List */}
        <div className="space-y-3">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 text-primary animate-spin" />
            </div>
          ) : filteredIssues.length === 0 ? (
            <Card className="orbit-card">
              <CardContent className="p-12 text-center">
                <AlertCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No issues found</p>
              </CardContent>
            </Card>
          ) : (
            filteredIssues.map((issue) => (
              <Link
                key={issue.id}
                to={`/issues/${issue.id}`}
                data-testid={`issue-row-${issue.issue_number}`}
              >
                <Card className="orbit-card orbit-card-interactive">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 flex-wrap">
                          <span className="font-mono text-sm text-primary font-medium">
                            {issue.issue_number}
                          </span>
                          <Badge className={getSeverityColor(issue.final_severity)}>
                            {issue.final_severity}
                          </Badge>
                          <Badge className={getStatusColor(issue.status)}>
                            {issue.status}
                          </Badge>
                          {issue.guest_type === "VIP" && (
                            <Badge className="bg-purple-500/10 text-purple-400 border-purple-500/20">
                              <Crown size={10} className="mr-1" /> VIP
                            </Badge>
                          )}
                          {issue.guest_status && (
                            <Badge className={getGuestStatusColor(issue.guest_status)}>
                              {getGuestStatusLabel(issue.guest_status)}
                            </Badge>
                          )}
                          {issue.is_recurring && (
                            <Badge className="bg-orange-500/10 text-orange-400 border-orange-500/20">
                              Recurring ({issue.recurring_count})
                            </Badge>
                          )}
                        </div>
                        <div className="mt-2 flex items-center gap-4 text-sm text-muted-foreground flex-wrap">
                          <span>Room {issue.room_number}</span>
                          <span>•</span>
                          <span>{issue.guest_name}</span>
                          <span>•</span>
                          <span className="uppercase text-xs">{issue.department_owner}</span>
                          {issue.booking_source && issue.booking_source !== "Walk-in" && (
                            <>
                              <span>•</span>
                              <span className="text-xs text-blue-400">{issue.booking_source}</span>
                            </>
                          )}
                        </div>
                        {/* Check In / Check Out Dates */}
                        {(issue.check_in_date || issue.check_out_date) && (
                          <div className="mt-1 flex items-center gap-3 text-xs text-muted-foreground">
                            {issue.check_in_date && (
                              <span className="flex items-center gap-1">
                                <span className="text-emerald-400">CI:</span>
                                {new Date(issue.check_in_date).toLocaleDateString('id-ID', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                              </span>
                            )}
                            {issue.check_out_date && (
                              <span className="flex items-center gap-1">
                                <span className="text-amber-400">CO:</span>
                                {new Date(issue.check_out_date).toLocaleDateString('id-ID', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                              </span>
                            )}
                          </div>
                        )}
                        <p className="mt-2 text-sm line-clamp-2">
                          {highlightText(issue.description)}
                        </p>
                        {issue.praised_departments?.length > 0 && (
                          <div className="mt-2 flex gap-1">
                            {issue.praised_departments.map((dept) => (
                              <Badge key={dept} className="bg-emerald-500/10 text-emerald-500 border-emerald-500/20 text-[10px]">
                                {dept} praised
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <div className="flex items-center gap-2">
                          {issue.images?.length > 0 && (
                            <div className="flex items-center gap-1 text-amber-400" title={`${issue.images.length} foto`}>
                              <Camera size={16} />
                              <span className="text-xs">{issue.images.length}</span>
                            </div>
                          )}
                          {issue.notified_at && (
                            <Smartphone size={16} className="text-blue-400" title="Notification sent" />
                          )}
                          {issue.first_response_at && (
                            <Eye size={16} className="text-emerald-400" title="Acknowledged" />
                          )}
                        </div>
                        <div className="text-right">
                          <p className={`font-heading text-lg font-bold ${getRiskColor(issue.risk_level)}`}>
                            {issue.risk_score}
                          </p>
                          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
                            Risk Score
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-mono text-[10px] text-muted-foreground">
                            <span className="text-amber-400">Terjadi:</span> {new Date(issue.incident_occurred_at || issue.system_logged_at).toLocaleDateString('id-ID', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                          </p>
                          <p className="font-mono text-[10px] text-muted-foreground opacity-60">
                            <span className="text-zinc-500">Dilaporkan:</span> {new Date(issue.system_logged_at).toLocaleDateString('id-ID', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))
          )}
        </div>
      </div>
    </Layout>
  );
};

export default CommandQueue;
